import { formatFilterValues } from '@/pages/dashboard/utils/utils';
import type { IRankProgressRow } from '@/pages/dashboard/components/RankProgressListCard/types';
import {
  mergeDimensionAllowlistIntoFilters,
  resolveUaNetworkFilterListForAllowlist,
} from '../KpiEventDimensionTrendService/_utils';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getMediaChannelIcon, mediaChannelMap } from '@/pages/home/_config/mediaChannal';
import Default from '@/pages/home/assets/svg/dashboard/default.svg';

/** 行首图标：国家旗（默认）或买量渠道 SVG（与首页 mediaChannal 一致） */
export type TNaturalRankIconMode = 'country' | 'ua_channel';

/** 与 dimension_name 平级的 custom_dimensions 项（如 ua_network 展示名） */
interface ICustomDimensionEntry {
  key?: string;
  value?: string;
  name_ch?: string;
  name_en?: string;
}

/** metrics_list 的单行（后端返回结构宽泛，以可选字段 + index signature 承载） */
export interface INaturalRankRawRow {
  metrics?: Record<string, unknown>;
  dimension_name?: Record<string, unknown>;
  custom_dimensions?: ICustomDimensionEntry[];
  country?: string;
  countryCode?: string;
  country_code?: string;
  country_name?: string;
  region_name?: string;
  zone_name?: string;
  ua_network?: string;
  ua_network_code?: string;
  [key: string]: unknown;
}

/** naturalRankBeforeRequest / buildNaturalNewRankRequest 的入参（Service 聚合后的原始 data） */
export interface INaturalRankReqData {
  filterVal?: Record<string, unknown> & {
    rangeTime?: string[];
    singleDaily?: string;
    start_date?: string;
    end_date?: string;
    period?: string;
    timeType?: string;
  };
  gameCode?: string;
  metrics?: string[];
  multi_sort?: Array<{ sort_by: string; desc?: boolean; }>;
  accumulation?: Record<string, unknown>;
  logical_flag?: string;
  page?: number;
  page_size?: number;
  uaNetworkMatchType?: string[];
  allowedUaNetworkCodes?: unknown;
  config?: {
    uaNetworkMatchType?: string[];
    allowedUaNetworkCodes?: unknown;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/** 不按日期分组，按国家维度排行（与 overall_dimension_top 语义一致） */
const DEFAULT_ACCUMULATION = {
  not_by_date: true,
  by_country: true,
};

function parseMetricNum(raw: unknown): number | '' {
  if (raw === undefined || raw === null || raw === '') {
    return '';
  }
  const n = Number(raw);
  return Number.isNaN(n) ? '' : n;
}

/**
 * 过滤 `metrics[valueMetricKey].value` 为 0、空或非数的行。
 * 须在 {@link metricsListToRankRows} 之前调用，以便占比按剩余行重算。
 * @param enabled 为 false 时原样返回（默认展示全部）
 */
export function filterMetricsListHideZeroOrEmptyValue(
  metricsList: INaturalRankRawRow[],
  valueMetricKey: string,
  enabled: boolean,
): INaturalRankRawRow[] {
  if (!enabled || !Array.isArray(metricsList) || metricsList.length === 0) {
    return metricsList;
  }
  return metricsList.filter(row => {
    const m = row?.metrics?.[valueMetricKey];
    // 后端返回 metrics[k] 形态为 { value, dod, sdlw } 等，结构断言用 Record<string, unknown>
    const bundle = (m && typeof m === 'object' ? m : {}) as Record<string, unknown>;
    const primary = parseMetricNum(bundle.value);
    if (primary === '') {
      return false;
    }
    if (typeof primary === 'number' && primary === 0) {
      return false;
    }
    return true;
  });
}

/**
 * overall_performance 请求体（与 KPI Overview 筛选器字段对齐）
 */
export function buildNaturalNewRankRequest(
  reqData: INaturalRankReqData,
): Record<string, unknown> | false {
  const fv = reqData?.filterVal || {};
  const gameCode = reqData?.gameCode;
  const {
    rangeTime,
    singleDaily,
    start_date: sd0,
    end_date: ed0,
    period: periodFromFilter,
    timeType,
    ...filters
  } = fv;

  let start_date = sd0;
  let end_date = ed0;
  if (singleDaily) {
    start_date = singleDaily;
    end_date = singleDaily;
  }
  if (Array.isArray(rangeTime) && rangeTime.length >= 2) {
    start_date = rangeTime[0];
    end_date = rangeTime[1];
  }

  if (!start_date || !end_date) {
    return false;
  }

  let period: string | undefined = periodFromFilter;
  if (!period && timeType) {
    if (timeType === 'Month') {
      period = 'monthly';
    } else if (timeType === 'Week') {
      period = 'weekly';
    } else {
      period = 'daily';
    }
  }
  if (!period) {
    period = 'daily';
  }

  const metrics = Array.isArray(reqData?.metrics) ? reqData.metrics : ['ug_register'];
  const multi_sort = Array.isArray(reqData?.multi_sort)
    ? reqData.multi_sort
    : [{ sort_by: String(metrics[0] ?? 'ug_register'), desc: true }];

  const isSingleDay = start_date === end_date;
  const rawAccumulation = reqData?.accumulation ?? DEFAULT_ACCUMULATION;
  const accumulation = {
    ...rawAccumulation,
    not_by_date: !isSingleDay,
  };
  const logical_flag =
    reqData?.logical_flag ?? 'overall_dimension_top';
  const page = Number(reqData?.page) || 1;
  const page_size = Number(reqData?.page_size) || 999;

  return {
    game_code: gameCode,
    filters: formatFilterValues(filters),
    start_date,
    end_date,
    period,
    metrics,
    multi_sort,
    accumulation,
    logical_flag,
    page,
    page_size,
  };
}

function asNonEmptyStringArray(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  const out = raw.map(x => String(x).trim()).filter(Boolean);
  return out.length > 0 ? out : undefined;
}

/** Service 与 string api 包装层识别：不发真实请求，格式化为空榜 */
export const NATURAL_RANK_SKIP_REQUEST_KEY = '_naturalRankSkipRequest';

/**
 * 在 overall_performance 请求体上叠加 ua_network 白名单（与 KpiEventDimensionTrend 的交集逻辑一致）。
 * 配置 `uaNetworkMatchType` 时由外层注入 `allowedUaNetworkCodes`；筛选选 255/未选子项时请求侧展开为完整白名单。
 * 用户选择与白名单无交集时返回跳过标记，由 Service 外包装的 api 返回空 metrics_list。
 */
export function naturalRankBeforeRequestWithUaNetworkAllowlist(
  data: INaturalRankReqData,
  buildBase: (d: INaturalRankReqData) => Record<string, unknown> | false = buildNaturalNewRankRequest,
): Record<string, unknown> | false {
  const base = buildBase(data);
  if (base === false) {
    return false;
  }
  const matchTypes =
    data?.uaNetworkMatchType ?? data?.config?.uaNetworkMatchType;
  const hasMatchTypeConfig =
    Array.isArray(matchTypes) && matchTypes.length > 0;
  const allowed = asNonEmptyStringArray(
    data?.allowedUaNetworkCodes ?? data?.config?.allowedUaNetworkCodes,
  );
  if (hasMatchTypeConfig && !allowed) {
    return { [NATURAL_RANK_SKIP_REQUEST_KEY]: true };
  }
  if (!allowed) {
    return base;
  }
  const fv = data?.filterVal as Record<string, unknown> | undefined;
  const uaNetworkList = resolveUaNetworkFilterListForAllowlist(fv, allowed);
  if (uaNetworkList === null) {
    return { [NATURAL_RANK_SKIP_REQUEST_KEY]: true };
  }
  const next: Record<string, unknown> = { ...base };
  const filters =
    next.filters && typeof next.filters === 'object' && !Array.isArray(next.filters)
      ? { ...(next.filters as Record<string, unknown>) }
      : {};
  const merged = mergeDimensionAllowlistIntoFilters(
    { ...filters },
    { uaNetworkList },
  );
  next.filters = merged;
  return next;
}

function pickRowDisplayName(row: INaturalRankRawRow, isEN: boolean): string {
  const dn = row?.dimension_name;
  if (dn && typeof dn === 'object') {
    const cn = String((dn as Record<string, unknown>).country_name_cn ?? '').trim();
    const en = String((dn as Record<string, unknown>).country_name_en ?? '').trim();
    if (isEN) {
      return en || cn || String(row.country_name ?? '').trim();
    }
    return cn || en || String(row.country_name ?? '').trim();
  }
  const name = row?.country_name ?? row?.region_name ?? row?.zone_name ?? '';
  return String(name).trim();
}

/**
 * overall_performance metrics_list 与 OverallTableCo / CommonTable 一致：
 * 国家维度 code 多在平级字段 `country`（或 countryCode），而非仅 dimension_name。
 */
function pickCountryCodeForFlag(row: INaturalRankRawRow): string {
  const flat =
    row?.country ?? row?.countryCode ?? row?.country_code;
  if (flat !== undefined && flat !== null && String(flat).trim() !== '') {
    const c = String(flat).trim();
    if (c === 'Total') {
      return 'Global';
    }
    return c;
  }
  const dn = row?.dimension_name as Record<string, unknown> | undefined;
  if (dn && typeof dn === 'object') {
    const code = dn.country_code ?? dn.region_code ?? dn.code;
    if (code !== undefined && code !== null && String(code).trim() !== '') {
      return String(code).trim();
    }
  }
  const cds = row?.custom_dimensions;
  if (Array.isArray(cds)) {
    const hit = cds.find(
      (x: ICustomDimensionEntry) =>
        x?.key === 'country' || x?.key === 'region',
    );
    const v = hit?.value;
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '';
}

/**
 * IVR / ua_network 排行：展示名优先取自与 dimension_name 平级的 custom_dimensions[]
 * 形如 [{ key: 'ua_network', value: '...', name_ch: '...', name_en: '...' }]
 */
function pickUaNetworkDisplayName(row: INaturalRankRawRow, isEN: boolean): string {
  const cds = row?.custom_dimensions;
  if (Array.isArray(cds)) {
    const hit = cds.find(
      (x: ICustomDimensionEntry) => x?.key === 'ua_network',
    );
    if (hit) {
      const primary = isEN ? hit.name_en : hit.name_ch;
      const fallback = isEN ? hit.name_ch : hit.name_en;
      const resolved = primary ?? fallback ?? hit.value;
      if (resolved !== undefined && resolved !== null && String(resolved).trim() !== '') {
        return String(resolved).trim();
      }
    }
  }
  const dn = row?.dimension_name as Record<string, unknown> | undefined;
  if (dn && typeof dn === 'object') {
    const cn = String(dn.ua_network_name_cn ?? '').trim();
    const en = String(dn.ua_network_name_en ?? '').trim();
    if (isEN) {
      return en || cn || String(row.ua_network ?? '').trim();
    }
    return cn || en || String(row.ua_network ?? '').trim();
  }
  return String(row?.ua_network ?? '').trim();
}

/** 与 OverallTable ua_network 列一致：平级 ua_network 或 custom_dimensions */
function pickUaNetworkChannelKey(row: INaturalRankRawRow): string {
  const flat = row?.ua_network ?? row?.ua_network_code;
  if (flat !== undefined && flat !== null && String(flat).trim() !== '') {
    return String(flat).trim();
  }
  const dn = row?.dimension_name as Record<string, unknown> | undefined;
  if (dn && typeof dn === 'object') {
    const code = dn.ua_network ?? dn.ua_network_code;
    if (code !== undefined && code !== null && String(code).trim() !== '') {
      return String(code).trim();
    }
  }
  const cds = row?.custom_dimensions;
  if (Array.isArray(cds)) {
    const hit = cds.find((x: ICustomDimensionEntry) => x?.key === 'ua_network');
    if (hit?.value) {
      return String(hit.value).trim();
    }
  }
  return '';
}

/**
 * 接口层 metrics 嵌套字段 → RankProgressListCard 行（`row.metrics` 按 key：natural_new / share / dod / sdlw）
 * 占比 share：前端按本榜各行主指标 value 求和后计算 value / sum（小数比率，与 absPercent1Decimal 一致），不读接口 share_off_all。
 */
export function metricsListToRankRows(
  metricsList: INaturalRankRawRow[],
  valueMetricKey: string,
): IRankProgressRow[] {
  if (!Array.isArray(metricsList) || metricsList.length === 0) {
    return [];
  }

  let maxPrimary = 0;
  const parsed = metricsList.map((row, idx) => {
    const m = row?.metrics?.[valueMetricKey];
    const bundle = (m && typeof m === 'object' ? m : {}) as Record<string, unknown>;
    const primary = parseMetricNum(bundle.value);
    const numPrimary = typeof primary === 'number' ? primary : 0;
    if (numPrimary > maxPrimary) {
      maxPrimary = numPrimary;
    }
    return { row, idx, primary, bundle, numPrimary };
  });

  const totalPrimary = parsed.reduce((sum, p) => {
    if (typeof p.primary === 'number' && !Number.isNaN(p.primary)) {
      return sum + p.primary;
    }
    return sum;
  }, 0);

  return parsed.map(({ idx, primary, bundle, numPrimary }) => {
    const share =
      totalPrimary > 0 &&
      typeof primary === 'number' &&
      !Number.isNaN(primary)
        ? primary / totalPrimary
        : '';
    const dod = parseMetricNum(bundle.dod);
    const sdlw = parseMetricNum(bundle.sdlw);
    const progressPercent =
      maxPrimary > 0 ? Math.round((numPrimary / maxPrimary) * 100) : 0;

    return {
      rank: idx + 1,
      name: '',
      progressPercent,
      metrics: {
        natural_new: { value: primary },
        share: { value: share },
        dod: { value: dod },
        sdlw: { value: sdlw },
      },
    };
  });
}

export function attachRowDisplayNames(
  rows: IRankProgressRow[],
  metricsList: INaturalRankRawRow[],
  isEN: boolean,
  options?: {
    iconMode?: TNaturalRankIconMode;
  },
): IRankProgressRow[] {
  const iconMode = options?.iconMode ?? 'country';

  return rows.map((r, i) => {
    const raw = metricsList[i];
    if (iconMode === 'ua_channel') {
      const key = pickUaNetworkChannelKey(raw);
      const channelIcon = getMediaChannelIcon(key);
      return {
        ...r,
        name: pickUaNetworkDisplayName(raw, isEN),
        iconUrl: channelIcon === mediaChannelMap._default ? Default : channelIcon,
      };
    }
    const code = pickCountryCodeForFlag(raw);
    return {
      ...r,
      name: pickRowDisplayName(raw, isEN),
      iconUrl: getCountryFlag(code),
    };
  });
}

/**
 * http 返回可能是 { code, data: { metrics_list } }，或已解包为带 metrics_list 的对象
 */
export function formatNaturalNewRankResponse(raw: unknown): {
  metrics_list: INaturalRankRawRow[];
} {
  if (!raw || typeof raw !== 'object') {
    return { metrics_list: [] };
  }
  // raw 可能是包了 code/data 的响应体，也可能是已解包的对象，按 Record 统一读取
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.metrics_list)) {
    return { metrics_list: obj.metrics_list as INaturalRankRawRow[] };
  }
  const inner = obj.data as Record<string, unknown> | undefined;
  if (inner && typeof inner === 'object' && Array.isArray(inner.metrics_list)) {
    return { metrics_list: inner.metrics_list as INaturalRankRawRow[] };
  }
  return { metrics_list: [] };
}

export { DEFAULT_ACCUMULATION };
